import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  profileService,
  UpdateProfessionalDataDto,
  ChangePasswordDto,
} from '@/features/profile/services/profile.service';
import { ProfessionalDataForm } from '@/features/profile/components/ProfessionalDataForm';
import { CompanyDataForm } from '@/features/profile/components/CompanyDataForm';
import { RpgProfileSection } from '@/features/profile/components/RpgProfileSection';
import { ChangePasswordCard } from '@/features/profile/components/ChangePasswordCard';
import { WaveLinkCard } from '@/features/profile/components/WaveLinkCard';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { AlertTriangle, CheckCircle2, User, Shield } from 'lucide-react';

export const Route = createFileRoute('/_authenticated/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: profileService.getProfile,
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateProfessionalDataDto) => profileService.updateProfessionalData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile-complete'] });
      toast({
        title: 'Dados salvos',
        description: 'Seus dados profissionais foram atualizados com sucesso.',
      });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar os dados. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: (data: Partial<UpdateProfessionalDataDto>) =>
      profileService.updateProfessionalData({
        fullName: profile?.professionalData?.fullName ?? '',
        address: profile?.professionalData?.address ?? '',
        phone: profile?.professionalData?.phone ?? '',
        rg: profile?.professionalData?.rg ?? '',
        cpf: profile?.professionalData?.cpf ?? '',
        email: profile?.professionalData?.email ?? '',
        birthDate: profile?.professionalData?.birthDate,
        pixKey: profile?.professionalData?.pixKey,
        ...data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      queryClient.invalidateQueries({ queryKey: ['profile-complete'] });
      toast({
        title: 'Dados salvos',
        description: 'Dados da empresa atualizados com sucesso.',
      });
    },
    onError: () => {
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar os dados. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: (data: ChangePasswordDto) => profileService.changePassword(data),
    onSuccess: () => {
      toast({
        title: 'Senha alterada',
        description: 'Sua senha foi atualizada com sucesso.',
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        'Não foi possível alterar a senha. Verifique os dados e tente novamente.';
      toast({
        title: 'Erro ao alterar senha',
        description: Array.isArray(message) ? message.join(', ') : message,
        variant: 'destructive',
      });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold">Meu Perfil</h1>
        <p className="text-muted-foreground">Gerencie seus dados pessoais e profissionais</p>
      </div>

      {/* Perfil RPG — no topo */}
      <RpgProfileSection />

      {!profile?.isProfileComplete && (() => {
        const professionalData = profile?.professionalData;
        const missingFields: string[] = [];
        
        if (!professionalData?.fullName) missingFields.push('Nome Completo');
        if (!professionalData?.address) missingFields.push('Endereço');
        if (!professionalData?.phone) missingFields.push('Telefone');
        if (!professionalData?.rg) missingFields.push('RG');
        if (!professionalData?.cpf) missingFields.push('CPF');
        if (!professionalData?.email) missingFields.push('Email');
        if (!professionalData?.birthDate) missingFields.push('Data de Nascimento');
        if (!professionalData?.pixKey?.trim()) missingFields.push('Chave PIX');
        if (profile?.employmentType === 'pj' && !professionalData?.cnpj?.trim()) {
          missingFields.push('CNPJ');
        }

        const missingPix = missingFields.includes('Chave PIX');
        const missingCount = missingFields.length;
        let message = '';
        
        if (missingPix && missingCount === 1) {
          message = 'Falta preencher a chave PIX.';
        } else if (missingCount === 1) {
          message = `Falta preencher: ${missingFields[0]}`;
        } else if (missingCount === 2) {
          message = `Faltam preencher: ${missingFields[0]} e ${missingFields[1]}`;
        } else if (missingCount > 2) {
          message = `Faltam preencher: ${missingFields.slice(0, -1).join(', ')} e ${missingFields[missingCount - 1]}`;
        } else {
          message = 'Por favor, preencha os dados do responsável legal para completar seu cadastro.';
        }

        return (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {missingPix ? 'Falta preencher a chave PIX' : 'Cadastro Incompleto'}
            </AlertTitle>
            <AlertDescription className="space-y-2">
              <p>{message}</p>
              {missingPix && missingCount > 1 && (
                <p>
                  Preencha a{' '}
                  <a href="#chave-pix" className="font-medium underline underline-offset-4">
                    Chave PIX
                  </a>{' '}
                  no formulário de dados pessoais abaixo.
                </p>
              )}
              {missingPix && missingCount === 1 && (
                <p>
                  Informe sua{' '}
                  <a href="#chave-pix" className="font-medium underline underline-offset-4">
                    Chave PIX
                  </a>{' '}
                  no formulário abaixo para completar o cadastro.
                </p>
              )}
            </AlertDescription>
          </Alert>
        );
      })()}

      {profile?.isProfileComplete && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800 dark:text-green-200">Cadastro Completo</AlertTitle>
          <AlertDescription className="text-green-700 dark:text-green-300">
            Todos os dados obrigatórios foram preenchidos.
          </AlertDescription>
        </Alert>
      )}

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Dados da Conta
          </CardTitle>
          <CardDescription>Informações básicas da sua conta</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Email</p>
              <p className="text-lg">{profile?.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Função</p>
              <Badge variant="outline" className="mt-1">
                {profile?.role}
              </Badge>
            </div>
          </div>
          <div>
            <p className="text-sm font-medium text-muted-foreground">Membro desde</p>
            <p>{profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString('pt-BR') : '-'}</p>
          </div>
        </CardContent>
      </Card>

      {/* Change Password */}
      <ChangePasswordCard
        onSubmit={async (data) => {
          await changePasswordMutation.mutateAsync(data);
        }}
        isLoading={changePasswordMutation.isPending}
      />

      <WaveLinkCard />

      {/* Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Permissões
          </CardTitle>
          <CardDescription>Suas permissões no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {profile?.permissions?.length ? (
              profile.permissions.map((perm) => (
                <Badge key={perm.name} variant="secondary" title={perm.description}>
                  {perm.name}
                </Badge>
              ))
            ) : (
              <p className="text-muted-foreground">Nenhuma permissão atribuída</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Professional Data Form */}
      <ProfessionalDataForm
        data={profile?.professionalData}
        onSubmit={async (data) => {
          await updateMutation.mutateAsync(data);
        }}
        isLoading={updateMutation.isPending}
      />

      {/* Dados da empresa (CNPJ etc.) — acessível a todos no perfil */}
      <CompanyDataForm
        data={profile?.professionalData}
        employmentType={profile?.employmentType}
        onSubmit={async (data) => {
          await updateCompanyMutation.mutateAsync(data);
        }}
        isLoading={isLoading || updateCompanyMutation.isPending}
        showPixKey={false}
      />
    </div>
  );
}

